
import * as React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import {
    VerificationService,
    TeacherPersonalInfoViewModel,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    TeacherPersonalInfoResponse,
    hasFailedInstantMatch,
    TeacherSchoolComponent,
    Organization,
    updateViewModelOrganization,
    updateFieldValidationErrorsByFieldId,
    handleEmailOnKeyDown,
    submitForm,
    VerificationStepsEnum,
    isFormFilled,
    setRef,
    HowDoesVerifyingWorkComponent,
    FirstNameComponent,
    LastNameComponent,
    EmailComponent,
    FormFooterComponent,
    BirthDateComponent as BirthDate,
    LogoComponent,
    FormSelectChoice,
    Locale,
    ChangeLocaleComponent,
    getAvailableLocaleChoices,
} from '@sheerid/jslib';

interface StepTeacherPersonalInfoComponentProps {
    verificationService: VerificationService;
    intl: any;
}

const StepTeacherPersonalInfo = ({ verificationService, intl }: StepTeacherPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as TeacherPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as TeacherPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
    const localeChoices: FormSelectChoice<Locale, string>[] = getAvailableLocaleChoices(
        verificationService.programTheme,
        intl,
    );
    const defaultLocaleChoice: FormSelectChoice<Locale, string> = {
        value: 'en-US',
        label: 'English',
    };

    const updateTeacherViewModel = (key: keyof TeacherPersonalInfoViewModel, value: any) => {
        const nextState: TeacherPersonalInfoViewModel = produce(viewModel, (draft: TeacherPersonalInfoViewModel) => {
            (draft[key] as any) = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const updateLocale = (localeChoice: FormSelectChoice<Locale, string>) => {
        const nextState: TeacherPersonalInfoViewModel = produce(
            viewModel,
            (draft: TeacherPersonalInfoViewModel) => {
                draft.localeChoice = localeChoice;
            },
        );
        verificationService.updateLocale(
            nextState,
            verificationService.programTheme,
            verificationService.verificationResponse.segment,
        );
    };

    // Don't validate DOB if ID Check is not enabled
    React.useEffect(() => {
        if (!verificationService.programTheme.idCheckAddOnEnabled) {
            updateTeacherViewModel('birthDate', '');
            updateTeacherViewModel('fieldsToSkipValidation', ['birthDate']);
        } else {
            updateTeacherViewModel('fieldsToSkipValidation', []);
        }
    }, [verificationService.programTheme.idCheckAddOnEnabled]);

    return (
        <div id="sid-step-teacher-personal-info" className="sid-l-container">


            { failedInstantMatch
                ? (
                    <div className="sid-header">
                        <div className="sid-l-horz-center">
                            <LogoComponent verificationService={verificationService} />
                        </div>
                        <div className="sid-header__title sid-l-horz-center">
                            <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name & Email" />
                        </div>
                        <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                            <FormattedHTMLMessage
                                id="step.personalInfo.tryAgain.subtitle"
                                defaultMessage="Your full name and email address must match what is on record with your school."
                            />
                        </div>
                    </div>
                ) : (
                    <div className="sid-header">
                        <div className="sid-l-horz-center">
                            <LogoComponent verificationService={verificationService} />
                        </div>
                        <div className="sid-header__title sid-l-horz-center">
                            <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="UNLOCK THIS TEACHER-ONLY OFFER" />
                        </div>
                        <div className="sid-header__subtitle sid-l-horz-center">
                            <FormattedHTMLMessage
                                id="step.personalInfo.subtitle"
                                defaultMessage="Verify you're a teacher, faculty or staff at a K-12 or degree-granting university."
                            />
                                &nbsp;
                            <HowDoesVerifyingWorkComponent verificationService={verificationService} />
                        </div>
                    </div>
                )}

            {localeChoices.length > 1 ? (
                <ChangeLocaleComponent
                    options={localeChoices}
                    value={viewModel.localeChoice || defaultLocaleChoice}
                    isErrored={false}
                    onChange={(localeChoice: FormSelectChoice<Locale, string>) => updateLocale(localeChoice)}
                />
            ) : null}

            <TeacherSchoolComponent
                value={(verificationService.viewModel as TeacherPersonalInfoViewModel).organization}
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                placeholder={intl.formatMessage({
                    id: 'schoolName',
                    defaultMessage: 'School name',
                })}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />

            <div className="sid-names">
                <FirstNameComponent
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    placeholder={intl.formatMessage({
                        id: 'firstName',
                        defaultMessage: 'First name',
                    })}
                    onChange={
                        (newValue: string) => {
                            updateTeacherViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastNameComponent
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    placeholder={intl.formatMessage({
                        id: 'lastName',
                        defaultMessage: 'Last name',
                    })}
                    onChange={
                        (newValue: string) => {
                            updateTeacherViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            {
                verificationService.programTheme.idCheckAddOnEnabled
                    ? (
                        <BirthDate
                            isErrored={!!fieldValidationErrors.birthDate}
                            errorId={fieldValidationErrors.birthDate}
                            onChange={(newValue: string) => {
                                updateTeacherViewModel('birthDate', newValue);
                                updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                            }}
                            value={viewModel.birthDate}
                        />
                    )
                    : null
            }

            <EmailComponent
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                placeholder={intl.formatMessage({
                    id: 'emailAddress',
                    defaultMessage: 'Email address (school-issued email address)',
                })}
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectTeacherPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        ref={(button: HTMLButtonElement) => setRef('submitButton', button)}
                    >
                        { failedInstantMatch
                            ? <FormattedHTMLMessage id="tryAgain" defaultMessage="TRY AGAIN" />
                            : <FormattedHTMLMessage id="verifyMyTeacherStatus" defaultMessage="VERIFY MY TEACHER STATUS" />}
                    </button>
                </div>
            </div>

            <FormFooterComponent verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="teacherInfoShared"
                    defaultMessage="Information entered into this web form will be used for verification purposes. The information will be shared with Levi's."
                />
                <div className="sid-l-space-top-sm" />
            </FormFooterComponent>
        </div>
    );
};

export const StepTeacherPersonalInfoComponent = injectIntl(StepTeacherPersonalInfo);
